// §4.4 messaging settings (Module 17, slice P3-B part 2) — ga/fa only.
//
// Two provider cards. TELEGRAM (live): bot username, account status chip,
// `last_webhook_at`, Reconnect (POST …/probe — the ChannelAccountProbeResponse
// carries the provider's own last-error text VERBATIM as a typed field, and
// it renders as-is, D-022/§4.4), Disconnect behind a confirm, and the connect
// wizard (token paste; getMe refusals render the server's own sentence plus a
// COPYABLE correlation id — §4.4's error-state rule). WHATSAPP: the D-232
// parked card — "Awaiting Meta verification + BSP selection" (spec §12.1) —
// with the embedded-signup control disabled and the reason beside it; the
// full card (tier, quality, template sync) lands at unparking.
//
// The health strip is GET /v1/messaging/health per account: §2.13 state chip,
// queued count, oldest-queued age, the rolling 5-min error rate, and "circuit
// opened" when down. §4.4 sketches a 24 h error-rate SPARKLINE; no route
// serves a time SERIES (the health read is the §2.13 point-in-time row), so
// the strip states the current rate instead of charting an invented history
// (D-033) — the series lands with module 13's facts, and the sparkline with
// it. Recorded as a slice deviation, not silently dropped.
//
// The webhook log tail: last ≤50 events (time, kind, processing_status);
// `payload` rides ONLY on error rows (contract), so the viewer opens on
// exactly those.
//
// ROLES (§7): `messaging.settings.read` / `messaging.account.manage` — both
// ga/fa. Everyone else has no nav entry (the entry table) and a deep link
// renders the refusal below (the sales access.* idiom).
//
// EMAIL INTAKE (P3-E2 PR E2-2e, D-327 rulings 5 and 19) is a third card. Off,
// it says what turning intake on does and offers Turn on; on, it shows the
// tenant's one fixed address with Copy, the forwarding line and Turn off. Its
// own health strip reads the email account's health item and account row: on
// or off from `account_status`, the receiving rule set as this tenant last
// probed or received it, and when the last mail arrived. Probe checks the
// rule set again. The Telegram and WhatsApp cards and the shared health strip
// skip the email account, which this card alone reads.

import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import {
  CardSkeleton,
  ErrorState,
  ForbiddenState,
  OfflineState,
} from "../../../../components/states";
import { requestIdOf, RequestError } from "../../../../lib/request";
import { formatTimestamp } from "../../../../lib/datetime";
import { formatRatioAsPercent } from "../../../../lib/quantity";
import { MutationFailureNote } from "../mutation-failure-note";
import { isMessagingReadRefused, useMessagingPermissions } from "../hooks/permissions";
import {
  useChannelAccounts,
  useConnectTelegram,
  useDisconnectAccount,
  useEmailIntake,
  useMessagingHealth,
  useMessagingOnline,
  useProbeAccount,
  useSetEmailIntake,
  useWebhookEvents,
  type ChannelAccount,
  type ChannelAccountProbe,
  type MessagingHealthSnapshot,
  type ProviderHealth,
  type WebhookEventLogItem,
} from "../hooks/messaging-queries";
import { relativeAge } from "../lib/format";

const ACCOUNT_CHIP: Record<ChannelAccount["status"], ChipVariant> = {
  connecting: "neutral",
  active: "ok",
  webhook_failing: "bad",
  suspended: "warn",
  disconnected: "neutral",
};

const HEALTH_CHIP: Record<MessagingHealthSnapshot["health"]["state"], ChipVariant> = {
  healthy: "ok",
  degraded: "warn",
  down: "bad",
};

/** numeric(5,4) fraction text → a percentage in the reader's format ("2.1%",
 * "2,1 %"). Text the read cannot parse shows as an em dash, never as itself. */
function errorRatePct(fraction: string, locale: string): string {
  return formatRatioAsPercent(fraction, { locale }) || "—";
}

export function MessagingSettingsScreen() {
  const { t, i18n } = useTranslation();
  const perms = useMessagingPermissions();
  const allowed = perms.canOpenSettings;
  const refused = isMessagingReadRefused(perms, allowed);
  const accounts = useChannelAccounts(allowed && !refused);
  const health = useMessagingHealth(allowed && !refused);
  const log = useWebhookEvents(allowed && !refused);
  const intake = useEmailIntake(allowed && !refused);

  if (refused) {
    return (
      <ForbiddenState title={t("messaging.access.title")} body={t("messaging.access.settings")} />
    );
  }
  /* The §5 render-once gate: `msg-settings-screen` is a walkthrough CHROME
   * selector (assertRenderedOnce, M17-S01), so its first paint must be its
   * settled shape — ONE skeleton gate for ALL THREE reads the paint derives
   * from (the work-order-detail rule), the Email intake card's read among
   * them. Gating on the accounts read alone let
   * the health strip and the log tail pop into their cards after first paint
   * (the 2026-09-01 staging class: content landing under an already-painted
   * testid). Each read settles fetched or FAILED — health and the log state
   * their own error inside their cards — so a red feed degrades to a stated
   * shape, never an eternal skeleton, and a paused read (offline) states
   * itself the same way. All three ride the one enabled flag above, so none
   * can be held disabled while another settles. No latch is needed: the keys
   * are stable, and the minute poll refreshes counts inside their reserved
   * rows without re-entering `isPending`. */
  if (accounts.isPending || health.isPending || log.isPending || intake.isPending) {
    if (
      (accounts.isPending && accounts.fetchStatus === "paused") ||
      (health.isPending && health.fetchStatus === "paused") ||
      (log.isPending && log.fetchStatus === "paused") ||
      (intake.isPending && intake.fetchStatus === "paused")
    ) {
      return (
        <OfflineState title={t("messaging.offline.title")} body={t("messaging.offline.body")} />
      );
    }
    return <CardSkeleton lines={6} label={t("messaging.common.loading")} />;
  }
  if (accounts.isError) {
    return (
      <ErrorState
        title={t("messaging.settings.error")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(accounts.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void accounts.refetch();
        }}
      />
    );
  }

  const telegram = accounts.data.find(
    (account) => account.provider === "telegram" && account.status !== "disconnected",
  );
  // An email account's `disconnected` is not terminal (D-327 ruling 5): intake
  // off keeps the one row, so the card reads it whatever its status.
  const emailAccount = accounts.data.find((account) => account.provider === "email");
  const emailHealth = health.data?.find((row) => row.provider === "email");

  return (
    <div className="msg-settings" data-testid="msg-settings-screen">
      <div className="msg-cards">
        <TelegramCard account={telegram} />
        <WhatsappCard />
        <EmailIntakeCard
          intake={intake}
          account={emailAccount}
          healthItem={emailHealth}
          canManage={perms.canManageAccounts}
        />
      </div>

      <HealthStrip health={health} locale={i18n.language} />

      <WebhookLog log={log} locale={i18n.language} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Telegram card (§3.1 connect wizard + §4.4 card)
// ---------------------------------------------------------------------------

function TelegramCard({ account }: { account: ChannelAccount | undefined }) {
  const { t } = useTranslation();
  const connect = useConnectTelegram();
  const probe = useProbeAccount();
  const disconnect = useDisconnectAccount();
  const [token, setToken] = useState("");
  // §3.1's wizard opens on demand — the not-connected card leads with ONE
  // action ("Connect Telegram") and the token form follows the click, so the
  // resting card is a statement, not a half-filled form.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [probeResult, setProbeResult] = useState<ChannelAccountProbe | undefined>(undefined);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  return (
    <Card title={t("messaging.provider.telegram")} data-testid="msg-tg-card">
      <Card.Body>
        {account === undefined ? (
          <>
            <p className="msg-hint" data-testid="msg-tg-not-connected">
              {t("messaging.settings.notConnected")}
            </p>
            {!wizardOpen ? (
              <div className="msg-toolbar">
                <Button
                  variant="primary"
                  size="sm"
                  data-testid="msg-connect-telegram"
                  onClick={() => {
                    setWizardOpen(true);
                  }}
                >
                  {t("messaging.settings.connectTitle")}
                </Button>
              </div>
            ) : (
              <>
                <h3>{t("messaging.settings.connectTitle")}</h3>
                <Field label={t("messaging.settings.connectToken")} wide>
                  <input
                    value={token}
                    placeholder={t("messaging.settings.connectTokenHint")}
                    data-testid="msg-token-input"
                    onChange={(e) => {
                      setToken(e.target.value);
                    }}
                  />
                </Field>
                <div className="msg-toolbar">
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="msg-connect-confirm"
                    disabled={token.trim().length < 20 || connect.isPending}
                    busy={connect.isPending}
                    busyLabel={t("messaging.settings.connecting")}
                    onClick={() => {
                      connect.mutate(token.trim(), {
                        onSuccess: () => {
                          setToken("");
                        },
                      });
                    }}
                  >
                    {t("messaging.settings.connectSubmit")}
                  </Button>
                </div>
              </>
            )}
            {connect.isError ? (
              <>
                {/* The server's own sentence (TG_TOKEN_INVALID carries getMe's
                 * words verbatim) + the copyable correlation id (§4.4). */}
                <MutationFailureNote
                  data-testid="msg-mutation-failed"
                  text={t("messaging.settings.connectFailed")}
                  error={connect.error}
                />
                {connect.error instanceof RequestError && connect.error.requestId !== undefined ? (
                  <Button
                    size="sm"
                    data-testid="msg-copy-request-id"
                    onClick={() => {
                      const id =
                        connect.error instanceof RequestError ? connect.error.requestId : undefined;
                      if (id !== undefined) void navigator.clipboard.writeText(id);
                    }}
                  >
                    {t("messaging.settings.copyRequestId")}
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <>
            <div className="msg-health-row">
              <StatusChip
                variant={ACCOUNT_CHIP[account.status]}
                data-testid="msg-account-status-chip"
                data-status={account.status}
              >
                {t(`messaging.settings.accountStatus.${account.status}`)}
              </StatusChip>
              {account.telegram_bot_username !== null ? (
                <span className="msg-fact">
                  {t("messaging.settings.botUsername")}:{" "}
                  <code data-testid="msg-tg-bot">@{account.telegram_bot_username}</code>
                </span>
              ) : null}
            </div>
            <p className="msg-fact num" data-testid="msg-tg-last-webhook">
              {account.last_webhook_at !== null
                ? t("messaging.settings.lastWebhook", {
                    time: relativeAge(t, account.last_webhook_at) ?? "",
                  })
                : t("messaging.settings.neverWebhook")}
            </p>
            <div className="msg-toolbar">
              <Button
                size="sm"
                data-testid="msg-probe-btn"
                busy={probe.isPending}
                busyLabel={t("messaging.settings.probing")}
                onClick={() => {
                  probe.mutate(account.id, {
                    onSuccess: (result) => {
                      setProbeResult(result);
                    },
                  });
                }}
              >
                {t("messaging.settings.reconnect")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                data-testid="msg-tg-disconnect"
                onClick={() => {
                  setConfirmDisconnect(true);
                }}
              >
                {t("messaging.settings.disconnect")}
              </Button>
            </div>
            {probeResult !== undefined ? (
              <div className="msg-link-block" data-testid="msg-probe-result">
                <p className="msg-fact">
                  {probeResult.webhook_ok
                    ? t("messaging.settings.probeOk")
                    : t("messaging.settings.probeNotOk")}
                </p>
                {probeResult.provider_detail !== null ? (
                  // The provider's own words, verbatim, as a typed field (§4.4).
                  <p className="msg-fact" data-testid="msg-provider-detail">
                    {t("messaging.settings.providerDetail", { text: probeResult.provider_detail })}
                  </p>
                ) : null}
              </div>
            ) : null}
            {probe.isError ? (
              <MutationFailureNote
                data-testid="msg-mutation-failed"
                text={t("messaging.settings.probeFailed")}
                error={probe.error}
              />
            ) : null}
            {disconnect.isError ? (
              <MutationFailureNote
                data-testid="msg-mutation-failed"
                text={t("messaging.settings.disconnectFailed")}
                error={disconnect.error}
              />
            ) : null}

            {confirmDisconnect ? (
              <Modal
                label={t("messaging.settings.disconnectTitle")}
                heading={t("messaging.settings.disconnectTitle")}
                closeLabel={t("messaging.common.close")}
                className="msg-modal"
                testId="msg-disconnect-confirm"
                onClose={() => {
                  setConfirmDisconnect(false);
                }}
              >
                <div className="msg-link-block">
                  <p className="msg-fact">{t("messaging.settings.disconnectBody")}</p>
                  <div className="msg-toolbar">
                    <Button
                      size="sm"
                      onClick={() => {
                        setConfirmDisconnect(false);
                      }}
                    >
                      {t("messaging.common.cancel")}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      data-testid="msg-tg-disconnect-confirm"
                      busy={disconnect.isPending}
                      busyLabel={t("messaging.settings.disconnecting")}
                      onClick={() => {
                        disconnect.mutate(account.id, {
                          onSuccess: () => {
                            setConfirmDisconnect(false);
                            setProbeResult(undefined);
                          },
                        });
                      }}
                    >
                      {t("messaging.settings.disconnect")}
                    </Button>
                  </div>
                </div>
              </Modal>
            ) : null}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

/** The D-232 parked card: the reason is a visible sentence and the signup
 * control is disabled BESIDE it — never a dead button alone. */
function WhatsappCard() {
  const { t } = useTranslation();
  return (
    <Card title={t("messaging.provider.whatsapp")} data-testid="msg-wa-card">
      <Card.Body>
        <StatusChip variant="warn" data-testid="msg-wa-parked">
          {t("messaging.settings.waParkedTitle")}
        </StatusChip>
        <p className="msg-hint">{t("messaging.settings.waParkedBody")}</p>
        <div className="msg-toolbar">
          <Button size="sm" disabled data-testid="msg-wa-signup">
            {t("messaging.settings.connectSubmit")}
          </Button>
          <p className="msg-hint">{t("messaging.settings.waSignupDisabled")}</p>
        </div>
      </Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Email intake card (§4.4, D-327 rulings 5 and 19)
// ---------------------------------------------------------------------------

/** The §2.13 row itself; the contract's named schema is nullable, because an
 * account's row is null only before its connect transaction commits. */
type HealthRow = NonNullable<ProviderHealth>;

const RULE_SET_CHIP: Record<HealthRow["state"], ChipVariant> = {
  healthy: "ok",
  degraded: "warn",
  down: "bad",
};

const RULE_SET_KEY: Record<HealthRow["state"], string> = {
  healthy: "messaging.settings.email.ruleSetActive",
  degraded: "messaging.settings.email.ruleSetUnknown",
  down: "messaging.settings.email.ruleSetInactive",
};

/** What a probe of the email account said, in the card's words. The detail
 * is a closed token the server writes (D-327 ruling 19), never a rule set's
 * name, so each one has its own sentence. */
function probeSentenceKey(result: ChannelAccountProbe): string {
  if (result.webhook_ok) return "messaging.settings.email.probeOk";
  return result.provider_detail === "ruleset_check_failed"
    ? "messaging.settings.email.probeUnreachable"
    : "messaging.settings.email.probeInactive";
}

/** When this tenant's copy of the rule set's state was taken: its last
 * success for an active rule set, its last failure otherwise. */
function checkedAt(health: HealthRow): string | null {
  return health.state === "healthy" ? health.last_success_at : health.last_failure_at;
}

function EmailIntakeCard({
  intake,
  account,
  healthItem,
  canManage,
}: {
  intake: ReturnType<typeof useEmailIntake>;
  account: ChannelAccount | undefined;
  healthItem: MessagingHealthSnapshot | undefined;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const online = useMessagingOnline();
  const toggle = useSetEmailIntake();
  const probe = useProbeAccount();
  const [confirmOff, setConfirmOff] = useState(false);
  const [copied, setCopied] = useState(false);
  const [probeResult, setProbeResult] = useState<ChannelAccountProbe | undefined>(undefined);

  let body: ReactNode;
  if (intake.isError) {
    body = (
      <ErrorState
        title={t("messaging.settings.email.error")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(intake.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void intake.refetch();
        }}
      />
    );
  } else if (intake.data === undefined) {
    body = null;
  } else {
    const { enabled, address, account_id: accountId } = intake.data;
    // The strip reads the health item first and the account row's own copy
    // of the same §2.13 row when the snapshot did not carry it.
    const health = healthItem?.health ?? account?.health ?? null;
    const status = healthItem?.account_status ?? account?.status;
    const switchFailure =
      toggle.error instanceof RequestError && toggle.error.code === "EMAIL_INTAKE_UNAVAILABLE";
    body = (
      <>
        {enabled && address !== null ? (
          <>
            <p className="msg-fact">{t("messaging.settings.email.address")}</p>
            <code className="msg-fact" data-testid="msg-email-address">
              {address}
            </code>
            <div className="msg-toolbar">
              <Button
                size="sm"
                data-testid="msg-email-copy"
                onClick={() => {
                  void navigator.clipboard.writeText(address).then(() => {
                    setCopied(true);
                  });
                }}
              >
                {t("messaging.settings.email.copy")}
              </Button>
              {copied ? (
                <span className="msg-hint" role="status" data-testid="msg-email-copied">
                  {t("messaging.common.copied")}
                </span>
              ) : null}
            </div>
            <p className="msg-hint" data-testid="msg-email-forward">
              {t("messaging.settings.email.forwardLine")}
            </p>
          </>
        ) : (
          <p className="msg-hint" data-testid="msg-email-off">
            {t("messaging.settings.email.offBody")}
          </p>
        )}

        {accountId !== null ? (
          <div className="msg-link-block" data-testid="msg-email-health">
            <div className="msg-health-row">
              <StatusChip
                variant={status === "active" ? "ok" : "neutral"}
                data-testid="msg-email-intake-chip"
                data-status={status ?? "disconnected"}
              >
                {status === "active"
                  ? t("messaging.settings.email.intakeOn")
                  : t("messaging.settings.email.intakeOff")}
              </StatusChip>
              {health !== null ? (
                <StatusChip
                  variant={RULE_SET_CHIP[health.state]}
                  data-testid="msg-email-ruleset"
                  data-state={health.state}
                >
                  {t(RULE_SET_KEY[health.state])}
                </StatusChip>
              ) : null}
            </div>
            {health === null ? (
              <p className="msg-hint" data-testid="msg-email-health-missing">
                {t("messaging.settings.email.healthMissing")}
              </p>
            ) : checkedAt(health) !== null ? (
              <p className="msg-fact num" data-testid="msg-email-checked">
                {t("messaging.settings.email.checkedAt", {
                  time: relativeAge(t, checkedAt(health)) ?? "",
                })}
              </p>
            ) : null}
            {health?.state === "down" ? (
              <p className="msg-error-note" data-testid="msg-email-not-receiving">
                {t("messaging.settings.email.probeInactive")}
              </p>
            ) : null}
            <p className="msg-fact num" data-testid="msg-email-last-mail">
              {account?.last_webhook_at !== null && account?.last_webhook_at !== undefined
                ? t("messaging.settings.email.lastMail", {
                    time: relativeAge(t, account.last_webhook_at) ?? "",
                  })
                : t("messaging.settings.email.noMail")}
            </p>
          </div>
        ) : null}

        {canManage ? (
          <div className="msg-toolbar">
            {enabled ? (
              <Button
                size="sm"
                variant="danger"
                data-testid="msg-email-turn-off"
                disabled={!online}
                onClick={() => {
                  toggle.reset();
                  setConfirmOff(true);
                }}
              >
                {t("messaging.settings.email.turnOff")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                data-testid="msg-email-turn-on"
                disabled={!online}
                busy={toggle.isPending}
                busyLabel={t("messaging.settings.email.turningOn")}
                onClick={() => {
                  probe.reset();
                  toggle.mutate(true);
                }}
              >
                {t("messaging.settings.email.turnOn")}
              </Button>
            )}
            {accountId !== null ? (
              <Button
                size="sm"
                data-testid="msg-email-probe"
                disabled={!online}
                busy={probe.isPending}
                busyLabel={t("messaging.settings.email.probing")}
                onClick={() => {
                  toggle.reset();
                  setProbeResult(undefined);
                  probe.mutate(accountId, {
                    onSuccess: (result) => {
                      setProbeResult(result);
                    },
                  });
                }}
              >
                {t("messaging.settings.email.probe")}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="msg-hint" data-testid="msg-email-read-only">
            {t("messaging.settings.email.readOnly")}
          </p>
        )}
        {canManage && !online ? (
          <p className="msg-hint" data-testid="msg-email-offline">
            {t("messaging.settings.email.offline")}
          </p>
        ) : null}

        {probeResult !== undefined ? (
          <p
            className={probeResult.webhook_ok ? "msg-fact" : "msg-error-note"}
            data-testid="msg-email-probe-result"
            data-ok={String(probeResult.webhook_ok)}
          >
            {t(probeSentenceKey(probeResult))}
          </p>
        ) : null}
        {probe.isError ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.settings.email.probeFailed")}
            error={probe.error}
          />
        ) : null}
        {switchFailure ? (
          // 409 EMAIL_INTAKE_UNAVAILABLE: this deployment has no inbound mail
          // source, a fact about the deployment and not a failed request.
          <p className="msg-error-note" role="alert" data-testid="msg-email-unavailable">
            {t("messaging.settings.email.unavailable")}
          </p>
        ) : toggle.isError && !confirmOff ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={
              toggle.variables
                ? t("messaging.settings.email.enableFailed")
                : t("messaging.settings.email.disableFailed")
            }
            error={toggle.error}
          />
        ) : null}

        {confirmOff ? (
          <Modal
            label={t("messaging.settings.email.turnOffTitle")}
            heading={t("messaging.settings.email.turnOffTitle")}
            closeLabel={t("messaging.common.close")}
            className="msg-modal"
            testId="msg-email-turn-off-confirm"
            pending={toggle.isPending}
            onClose={() => {
              setConfirmOff(false);
            }}
          >
            <div className="msg-link-block">
              <p className="msg-fact">{t("messaging.settings.email.turnOffBody")}</p>
              <div className="msg-toolbar">
                <Button
                  size="sm"
                  onClick={() => {
                    setConfirmOff(false);
                  }}
                >
                  {t("messaging.common.cancel")}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  data-testid="msg-email-turn-off-confirm-btn"
                  disabled={!online}
                  busy={toggle.isPending}
                  busyLabel={t("messaging.settings.email.turningOff")}
                  onClick={() => {
                    toggle.mutate(false, {
                      onSuccess: () => {
                        setConfirmOff(false);
                        setProbeResult(undefined);
                        setCopied(false);
                      },
                    });
                  }}
                >
                  {t("messaging.settings.email.turnOff")}
                </Button>
              </div>
              {toggle.isError && !switchFailure ? (
                <MutationFailureNote
                  data-testid="msg-mutation-failed"
                  text={t("messaging.settings.email.disableFailed")}
                  error={toggle.error}
                />
              ) : null}
            </div>
          </Modal>
        ) : null}
      </>
    );
  }

  return (
    <Card title={t("messaging.settings.email.title")} data-testid="msg-email-card">
      <Card.Body>{body}</Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// health strip
// ---------------------------------------------------------------------------

/** The shared strip lists the chat accounts: queue posture and error rate
 * describe sending, and the email account sends nothing. Its state is the
 * Email intake card's own strip (D-327 ruling 19). */
function chatRows(rows: readonly MessagingHealthSnapshot[]): MessagingHealthSnapshot[] {
  return rows.filter((row) => row.provider !== "email");
}

function HealthStrip({
  health,
  locale,
}: {
  health: ReturnType<typeof useMessagingHealth>;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <Card title={t("messaging.settings.healthTitle")} data-testid="msg-health">
      <Card.Body>
        {/* §4.4 sketches a 24 h error-rate sparkline; no route serves a time
         * series (the health read is the §2.13 point-in-time row), so the
         * strip shows the current rate and nothing stands in for the line
         * (P3-UX, D-425: an absent feature renders nothing). */}
        {health.isPending ? (
          <CardSkeleton lines={2} label={t("messaging.common.loading")} />
        ) : health.isError ? (
          <ErrorState
            title={t("messaging.settings.error")}
            retryLabel={t("messaging.common.retry")}
            requestId={requestIdOf(health.error)}
            requestIdLabel={t("messaging.common.requestId")}
            onRetry={() => {
              void health.refetch();
            }}
          />
        ) : chatRows(health.data).length === 0 ? (
          <p className="msg-hint" data-testid="msg-health-empty">
            {t("messaging.settings.healthEmpty")}
          </p>
        ) : (
          chatRows(health.data).map((row) => (
            <div
              className="msg-health-row"
              key={row.channel_account_id}
              data-testid={`msg-health-${row.provider}`}
            >
              <span className="msg-provider">{t(`messaging.provider.${row.provider}`)}</span>
              <StatusChip
                variant={HEALTH_CHIP[row.health.state]}
                data-testid="msg-health-chip"
                data-state={row.health.state}
              >
                {t(`messaging.settings.healthState.${row.health.state}`)}
              </StatusChip>
              <span className="msg-fact num" data-testid="msg-health-queued">
                {t("messaging.settings.queuedCount", { count: row.queued_count })}
              </span>
              {row.oldest_queued_at !== null ? (
                <span className="msg-fact num">
                  {t("messaging.settings.oldestQueued", {
                    time: relativeAge(t, row.oldest_queued_at) ?? "",
                  })}
                </span>
              ) : null}
              <span className="msg-fact num" data-testid="msg-health-error-rate">
                {t("messaging.settings.errorRate", {
                  pct: errorRatePct(row.health.window_error_rate, locale),
                })}
              </span>
              {row.health.state === "down" && row.health.opened_at !== null ? (
                <span className="msg-fact num" data-testid="msg-circuit-opened">
                  {t("messaging.settings.circuitOpened", {
                    time: formatTimestamp(row.health.opened_at, locale),
                  })}
                </span>
              ) : null}
            </div>
          ))
        )}
      </Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// webhook log tail
// ---------------------------------------------------------------------------

function WebhookLog({ log, locale }: { log: ReturnType<typeof useWebhookEvents>; locale: string }) {
  const { t } = useTranslation();
  const [payloadRow, setPayloadRow] = useState<WebhookEventLogItem | undefined>(undefined);

  const columns: DataTableColumn<WebhookEventLogItem>[] = [
    {
      key: "time",
      header: t("messaging.settings.colTime"),
      age: true,
      render: (row) => formatTimestamp(row.received_at, locale),
    },
    {
      key: "kind",
      header: t("messaging.settings.colKind"),
      // A typed classification, localized — never the raw token (D-225); the
      // parity gate's dynamic family holds these four against the contract.
      render: (row) => t(`messaging.settings.webhookKind.${row.kind}`),
    },
    {
      key: "status",
      header: t("messaging.settings.colStatus"),
      render: (row) => (
        <StatusChip
          variant={
            row.processing_status === "error"
              ? "bad"
              : row.processing_status === "processed"
                ? "ok"
                : "neutral"
          }
          dot={false}
          data-status={row.processing_status}
        >
          {t(`messaging.settings.processing.${row.processing_status}`)}
        </StatusChip>
      ),
    },
    {
      key: "actions",
      header: "",
      label: t("messaging.settings.viewPayload"),
      actions: true,
      render: (row) =>
        row.processing_status === "error" && row.payload !== undefined ? (
          <Button
            size="sm"
            data-testid="msg-log-payload"
            onClick={() => {
              setPayloadRow(row);
            }}
          >
            {t("messaging.settings.viewPayload")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card title={t("messaging.settings.logTitle")} data-testid="msg-webhook-log">
      {log.isError ? (
        <ErrorState
          title={t("messaging.settings.logError")}
          retryLabel={t("messaging.common.retry")}
          requestId={requestIdOf(log.error)}
          requestIdLabel={t("messaging.common.requestId")}
          onRetry={() => {
            void log.refetch();
          }}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={log.data ?? []}
          rowKey={(row) => row.id}
          caption={t("messaging.settings.logCaption")}
          loading={log.isPending}
          loadingLabel={t("messaging.common.loading")}
          empty={t("messaging.settings.logEmpty")}
          rowTestId="msg-webhook-log-row"
          // §2.10's dedup key on every row: the walkthrough contract binds
          // "the run's update_id appears on EXACTLY one row" to this
          // attribute (M17-S12; the §5 row names provider_event_id).
          rowProps={(row) => ({ "data-provider-event-id": row.provider_event_id })}
        />
      )}

      {payloadRow !== undefined ? (
        <Modal
          label={t("messaging.settings.payloadTitle")}
          heading={t("messaging.settings.payloadTitle")}
          closeLabel={t("messaging.common.close")}
          className="msg-modal"
          testId="msg-payload-modal"
          onClose={() => {
            setPayloadRow(undefined);
          }}
        >
          {payloadRow.error !== null ? <p className="msg-error-note">{payloadRow.error}</p> : null}
          <pre className="msg-payload" data-testid="msg-payload-json">
            {JSON.stringify(payloadRow.payload, null, 2)}
          </pre>
        </Modal>
      ) : null}
    </Card>
  );
}
